export default {
  code: "en",
  brand: { name: "Tonouh", word: "Marketing" },
  nav: {
    home: "Home",
    services: "Our Services",
    about: "About",
    contact: "Contact",
    cta: "Contact us",
    menu: "Open menu",
    close: "Close menu",
  },
  hero: {
    eyebrow: "Marketing & consultancy for the childcare sector",
    titleLines: ["Specialist marketing", "and consultancy for", "childcare organisations."],
    italicLine: 2,
    sub: "Tonouh Marketing helps childcare organisations strengthen their positioning, reach parents more effectively and work in a focused way on growth and organisational development. With a particular focus on the Dutch childcare market.",
    primaryCta: "Discuss your challenge",
    secondaryCta: "Explore our services",
    note: "Based in Ras Al Khaimah (UAE)",
    note2: "Working across Europe",
    badgeMarkets: "Netherlands · Belgium · Germany · France · Scandinavia",
    imgAlt: "Children working at a table in a warm, professional childcare classroom",
  },
  marquee: ["Parent trust", "Local visibility", "Occupancy", "Team & leadership", "Consistent communication", "Sustainable growth"],
  home: {
    intro: {
      eyebrow: "Introduction",
      title: "A partner that understands childcare — and growth.",
      p1: "Tonouh Marketing FZ LLC is a specialised marketing and consultancy firm for the childcare sector. We work with owners, directors and management teams of day-care centres and childcare organisations — from single locations to organisations with multiple sites.",
      p2: "Whether you want to sharpen your market position, attract more relevant enquiries, strengthen communication with parents or address organisational questions: we help you turn ambitions into a clear, workable approach.",
      imgAlt: "Professionals in discussion around a meeting table",
      link: "More about us",
    },
    categories: {
      eyebrow: "What we do",
      title: "Three directions, one focus: childcare.",
      items: [
        { id: "marketing", index: "01", icon: "megaphone", title: "Marketing & Positioning", desc: "Stand out in your local market. From market and audience analysis to brand strategy, online visibility and campaigns that reach parents — and turn interest into tours and enrolments.", link: "Explore this service" },
        { id: "management", index: "02", icon: "briefcase", title: "Management & Operations", desc: "Strategy, leadership and organisation. Practical advice on growth plans, management, processes, staffing and expansion — built around how childcare organisations really work.", link: "Explore this service" },
        { id: "assessments", index: "03", icon: "chart", title: "Assessments & Insights", desc: "Understand how parents and staff experience your organisation. Satisfaction surveys and organisational analysis, translated into a practical improvement plan.", link: "Explore this service" },
      ],
    },
    specialization: {
      eyebrow: "Sector expertise",
      title: "Why childcare is a different market.",
      intro: "Childcare is not a typical service market. Parents make one of the most important decisions of their lives and choose an organisation they trust. That asks for a different approach than generic marketing.",
      items: [
        { icon: "shield", title: "Parent trust", desc: "Parents choose with both heart and head. Trust is the deciding factor — built through consistency, transparency and personal attention." },
        { icon: "map", title: "Local visibility", desc: "Parents search close to home or work. Local visibility determines whether they find you at all." },
        { icon: "target", title: "Occupancy", desc: "Every unfilled place is structural loss. Occupancy follows from positioning, findability and a well-organised enrolment journey." },
        { icon: "users", title: "Staff & leadership", desc: "Qualified staff are scarce everywhere. How you present yourself as an employer matters as much as how you present yourself to parents." },
        { icon: "messages", title: "Consistent communication", desc: "From first enquiry to daily updates: every moment of contact shapes the parent experience — and with it your reputation." },
      ],
      imgAlt: "A calm, well-organised nursery interior",
    },
    approach: {
      eyebrow: "How we work",
      title: "A clear, calm way of working.",
      steps: [
        { k: "01", title: "Introduction", desc: "We start with an open, no-obligation conversation about your organisation, your market and your question." },
        { k: "02", title: "Analysis", desc: "We map your position, your parents, your visibility and your processes." },
        { k: "03", title: "Defining the approach", desc: "Together we set priorities and a concrete plan that fits your organisation." },
        { k: "04", title: "Support & evaluation", desc: "We support implementation, evaluate what works and adjust where needed." },
      ],
    },
    europe: {
      eyebrow: "Work area",
      title: "European focus. Dutch specialisation.",
      p1: "Our work area is Europe, with a clear specialisation in the Dutch childcare market — a market with its own dynamics, funding system and high expectations from parents. We also work with organisations in Belgium, Germany, France and Scandinavia.",
      p2: "Tonouh is headquartered in Ras Al Khaimah, United Arab Emirates, and collaborates with organisations across Europe — remotely and hands-on.",
      marketsLabel: "Focus markets",
      markets: ["Netherlands", "Belgium", "Germany", "France", "Scandinavia"],
    },
    closing: {
      title: "What challenge is your organisation facing?",
      body: "Tell us about your childcare organisation. We will think along with you — openly, practically and without obligation.",
      cta: "Discuss your challenge",
      email: "Or write to",
    },
  },
  services: {
    hero: {
      eyebrow: "Our services",
      title: "Marketing and consultancy, built for childcare.",
      sub: "Three service categories with one starting point: your organisation, your parents and your market. Every engagement begins with understanding — and ends in practical, actionable advice.",
    },
    categories: [
      {
        id: "marketing",
        index: "01",
        name: "Marketing & Positioning",
        tagline: "Be found, be chosen.",
        intro: "How parents perceive you — and whether they find you at all. We help you build a distinctive position in your local market and reach the right parents with the right message.",
        imgAlt: "A bright childcare playroom with small tables and chairs",
        items: [
          "Market and audience analysis",
          "Brand strategy and positioning",
          "Website content and online visibility",
          "Local marketing and campaigns",
          "Communication with parents",
          "Analysis of the journey from first interest to tour and enrolment",
          "Strategies for attracting relevant enquiries and improving occupancy",
        ],
        cta: "Discuss marketing & positioning",
      },
      {
        id: "management",
        index: "02",
        name: "Management & Organisation Advisory",
        tagline: "Organisation, leadership, growth.",
        intro: "Growing childcare organisations face questions that go beyond marketing: leadership, processes, staffing and expansion. We advise on the organisational side — with knowledge of the sector.",
        imgAlt: "A team of professionals in discussion at a conference table",
        items: [
          "Strategic growth plans",
          "Advice on management and leadership",
          "Support through organisational change",
          "Analysis and improvement of work processes",
          "Advice on staffing planning and employee retention",
          "Support with expansion or new locations",
        ],
        cta: "Discuss management & organisation",
      },
      {
        id: "assessments",
        index: "03",
        name: "Assessments & Insights",
        tagline: "Insight before action.",
        intro: "How do parents experience your organisation? Where do processes run smoothly and where do they stall? We map it objectively and translate findings into a practical improvement plan.",
        imgAlt: "A childcare professional talking with a parent and child",
        items: [
          "Parent satisfaction surveys",
          "Parent experience analysis",
          "Organisation and location analysis",
          "Evaluation of communication, processes and collaboration",
          "Practical improvement plans based on findings",
        ],
        cta: "Request an assessment",
      },
    ],
    advisory: {
      title: "Advisory in nature",
      body: "Our assessments and analyses provide insight and direction. They are not official supervisory inspections and do not lead to certification.",
    },
    uae: {
      eyebrow: "Beyond Europe",
      title: "Starting childcare in the UAE?",
      intro: "Exploring the start-up market in the United Arab Emirates? We support the preparation with research and positioning — the same structured approach we apply in Europe.",
      items: ["Feasibility studies for new childcare locations in the UAE", "Market analysis and positioning", "Support with preparation and launch"],
      note: "Advisory support for market entry. No outcome — including licence approval — can be guaranteed.",
      cta: "Discuss your UAE plans",
      interest: "uae",
    },
  },
  about: {
    hero: {
      eyebrow: "About us",
      title: "Marketing and consultancy, dedicated to childcare.",
      sub: "Tonouh Marketing FZ LLC combines marketing with organisational advisory — for childcare organisations across Europe, with a clear specialisation in the Netherlands.",
    },
    story: {
      eyebrow: "Our story",
      title: "Built on understanding, not templates.",
      paras: [
        "Tonouh Marketing FZ LLC is a specialised marketing and consultancy firm for the childcare sector. From Ras Al Khaimah in the United Arab Emirates, we work with owners, directors and management teams across Europe — with a clear specialisation in the Dutch childcare market.",
        "Childcare organisations face two kinds of questions. How do we become and stay visible to parents, and how do we present a strong, distinctive position? And: how do we organise growth, management and daily operations in a sector where quality and trust are everything? Tonouh deliberately combines both: marketing and organisational advisory under one roof.",
        "Our approach always starts with understanding — of your organisation, your parents, your team and your local market. Only then do we advise. No standard playbooks, but a practical approach that fits how childcare works and that your team can put to work immediately.",
      ],
      imgAlt: "Colleagues in conversation during a working session",
    },
    values: {
      eyebrow: "Core values",
      title: "How we work with you.",
      items: [
        { icon: "message", title: "Clear communication", desc: "Honest advice, plain language and realistic expectations. No jargon, no illusions." },
        { icon: "handshake", title: "Commitment", desc: "We take a genuine interest in your organisation. Your question becomes our question — until it is resolved." },
        { icon: "list", title: "Practical advice", desc: "Recommendations you can act on: concrete, prioritised and feasible with your team." },
        { icon: "sprout", title: "Sustainable development", desc: "Growth that lasts. We build positions and organisations that stay strong in the long term." },
      ],
    },
    location: {
      eyebrow: "Where we are",
      title: "Ras Al Khaimah, United Arab Emirates.",
      body: "Our home base is in the UAE; our focus is Europe. We collaborate with childcare organisations remotely, in structured trajectories with clear agreements and short lines of communication.",
      cta: "Get in touch",
    },
  },
  contact: {
    hero: {
      eyebrow: "Contact & enquiry",
      title: "Tell us about your childcare organisation",
      sub: "Whether you have a concrete question or are still exploring: leave your details, share your challenge and we will respond personally and thoughtfully.",
    },
    info: {
      emailLabel: "Email",
      locationLabel: "Based in",
      locationValue: "Ras Al Khaimah, United Arab Emirates",
      areaLabel: "Work area",
      areaValue: "Europe — with a specialisation in the Netherlands",
      confidentiality: "We treat your information confidentially and use it only to respond to your enquiry.",
    },
    form: {
      name: "Name",
      namePh: "Your name",
      organization: "Organisation name",
      organizationPh: "Your organisation",
      email: "Business email address",
      emailPh: "name@organisation.com",
      phone: "Phone number (optional)",
      phonePh: "+31 …",
      country: "Country",
      countryPh: "Select your country",
      countries: ["Netherlands", "Belgium", "Germany", "France", "Luxembourg", "Denmark", "Sweden", "Norway", "Finland", "Iceland", "Austria", "Switzerland", "Spain", "Italy", "Portugal", "Ireland", "United Kingdom", "United Arab Emirates", "Other"],
      website: "Website (optional)",
      websitePh: "www.yourorganisation.com",
      locations: "Number of locations (optional)",
      locationsPh: "Select",
      locationOptions: ["1", "2–5", "6–10", "More than 10"],
      interest: "Area of interest",
      interestPh: "Select",
      interestOptions: [
        { v: "general", label: "General question" },
        { v: "marketing", label: "Marketing & Positioning" },
        { v: "management", label: "Management & Operations" },
        { v: "assessments", label: "Assessments & Insights" },
        { v: "uae", label: "Starting childcare in the UAE" },
      ],
      message: "Your question or challenge",
      messagePh: "Briefly describe your organisation and what you would like help with.",
      consent: "Keep me informed of new services and insights from Tonouh Marketing (optional).",
      privacyNote: "By submitting this form you agree to the processing of your details as described in our",
      privacyLink: "privacy statement",
      required: "Required",
      honeypot: "Fax",
      submit: "Send your enquiry",
      sending: "Sending…",
      errors: {
        name: "Please enter your name.",
        organization: "Please enter your organisation's name.",
        email: "Please enter a valid business email address.",
        country: "Please select your country.",
        message: "Please describe your question or challenge (at least 20 characters).",
      },
      success: {
        title: "Thank you — your enquiry has been received.",
        body: "We will review your message and respond personally to your email address.",
        again: "Send another enquiry",
      },
      errorGeneral: "Something went wrong while sending. Your details have been kept — please try again.",
      errorRate: "You have sent several enquiries in a short time. Please try again later.",
    },
  },
  privacy: {
    title: "Privacy statement",
    draftBadge: "Draft — to be verified before publication",
    intro: "This draft privacy statement describes how Tonouh Marketing FZ LLC handles personal data, based on the data processing as actually built on this website. Details marked [to be completed] must be verified before publication.",
    sections: [
      { h: "1. Who is responsible?", ps: ["Tonouh Marketing FZ LLC, based in Ras Al Khaimah, United Arab Emirates, is the data controller for the processing described below. [Full postal address and company registration number — to be completed.] Questions and requests: info@tonouhmarketing.com."] },
      { h: "2. What data do we collect?", ps: ["When you complete the enquiry form on this website, we collect:"], list: ["Your name and the name of your organisation", "Your business email address", "Your phone number (optional)", "Your country", "Your website (optional)", "The number of locations of your organisation (optional)", "Your area of interest", "Your question or challenge", "Whether you have given permission for marketing updates (optional)"], ps2: ["In addition, we store the IP address and time of submission with each enquiry. We use this only for spam protection and security. We do not ask for — and we ask you not to send us — any information about individual children."] },
      { h: "3. Why do we use your data?", list: ["To respond to your question or challenge", "To store and follow up on your enquiry within our organisation", "To protect our website against spam and misuse (legitimate interest)", "To send you occasional updates on our services and insights, but only if you have explicitly given permission"], ps: ["We do not use your data for any other purpose and never sell it to third parties."] },
      { h: "4. Legal basis", ps: ["We process your data on the basis of our legitimate interest in handling and answering your enquiry. Marketing updates are sent only on the basis of your explicit consent, which you can withdraw at any time via the same e-mail address."] },
      { h: "5. How long do we keep your data?", ps: ["Enquiries are kept as long as necessary to handle them and any follow-up, and in any case no longer than [retention period — to be confirmed] after our last contact with you."] },
      { h: "6. Who has access to your data?", ps: ["Your data is stored in the database of our hosting provider and is only accessible to Tonouh Marketing. Our hosting provider may process data outside the European Economic Area; we ensure appropriate safeguards. When you submit an enquiry, we also receive its details by e-mail, sent through our e-mail provider. We do not share your data with any other third party."] },
      { h: "7. Cookies and tracking", ps: ["This website does not use tracking or advertising cookies and does not run analytics without your consent."] },
      { h: "8. Your rights", ps: ["You have the right to access, correct or delete your data, to object to processing and to withdraw consent. Send your request to info@tonouhmarketing.com and we will respond as soon as possible. If you believe we are handling your data incorrectly, you also have the right to lodge a complaint with the relevant supervisory authority."] },
      { h: "9. Changes to this statement", ps: ["We may update this privacy statement when our processing changes. The most recent version is always published on this page."] },
    ],
  },
  footer: {
    tagline: "Marketing and consultancy for childcare organisations — focused on Europe, specialised in the Netherlands.",
    navTitle: "Navigation",
    contactTitle: "Contact",
    emailLabel: "info@tonouhmarketing.com",
    location: "Ras Al Khaimah, United Arab Emirates",
    area: "Work area: Europe",
    privacy: "Privacy statement",
    rights: "© {year} Tonouh Marketing FZ LLC. All rights reserved.",
  },
  notFound: {
    title: "Page not found",
    body: "The page you are looking for does not exist or has moved.",
    cta: "Back to home",
  },
  meta: {
    home: { title: "Specialist Marketing & Consultancy for Childcare | Tonouh", description: "Tonouh Marketing helps childcare organisations strengthen their positioning, reach parents and grow. Specialist in the Dutch childcare market." },
    services: { title: "Childcare Marketing & Consultancy Services | Tonouh", description: "Marketing & positioning, management and organisation advisory, assessments and insights — tailored to childcare organisations in Europe and the Netherlands." },
    about: { title: "About Tonouh Marketing — Childcare Marketing & Consultancy", description: "Tonouh Marketing FZ LLC combines marketing with organisational advisory for childcare organisations across Europe, based in Ras Al Khaimah, UAE." },
    contact: { title: "Tell Us About Your Childcare Organisation | Tonouh", description: "Share your challenge with Tonouh Marketing. Specialists in marketing and consultancy for childcare organisations in Europe and the Netherlands." },
    privacy: { title: "Privacy Statement | Tonouh Marketing", description: "How Tonouh Marketing collects, uses and protects your details when you contact us through our website." },
  },
};
